import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, Clock, ArrowLeft, ArrowRight } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { SEO } from '../../components/common/SEO';

// This would normally come from a CMS or API
const blogPosts = [
  {
    slug: 'christmas-gift-guide-2025',
    title: 'Ultimate Christmas Gift Guide 2025: 100+ Perfect Gift Ideas',
    description: 'Discover the perfect Christmas gifts for everyone on your list. From budget-friendly stocking stuffers to luxury presents, plus money-saving tips!',
    content: `
# Ultimate Christmas Gift Guide 2025: 100+ Perfect Gift Ideas

Christmas is magical, but finding the perfect gift for everyone on your list? That can feel overwhelming. Don't worry – we've curated the ultimate Christmas gift guide with 100+ ideas for every person, budget, and occasion.

## 🎁 Gifts Under $25

Perfect for stocking stuffers, Secret Santa, or when you're shopping on a budget:

### Cozy Comfort Gifts
- **Fuzzy Reading Socks** - Because everyone deserves warm feet while reading
  - Perfect for: Book lovers, students, anyone who gets cold feet
  - Price range: $12-18

- **Aromatherapy Shower Steamers** - Turn any shower into a spa experience  
  - Perfect for: Busy parents, college students, stress relief
  - Price range: $8-15

## 💡 Money-Saving Christmas Shopping Tips

### 1. Start Your Wishlist Early
Create wishlists throughout the year as you discover things you want. Share them with family so they know exactly what to get you!

### 2. Track Prices  
Many items go on sale in November and December. Use price tracking tools to know when to buy.

### 3. Shop During Off-Peak Times
Avoid the crowds and often find better deals by shopping early in the morning or late at night.

## 🎯 Gift Ideas by Recipient

### For Him
- Tech gadgets and accessories
- Quality tools and equipment
- Sports memorabilia
- Gourmet food and drinks

### For Her
- Jewelry and accessories
- Spa and beauty products
- Home decor items
- Books and journals

### For Kids
- Educational toys
- Art supplies
- Outdoor equipment
- Books and games

---

*Want to organize your gift planning? Create a free wishlist on Wishful and share it with friends and family!*
    `,
    publishedAt: '2024-11-01',
    author: 'Wishful Team',
    readingTime: '12 min read',
    image: '/images/blog/christmas-gift-guide-2025.jpg',
    imageAlt: 'Beautiful Christmas gifts under a decorated tree',
    category: 'Gift Guides',
    tags: ['christmas', 'gift guide', 'holiday shopping', '2025'],
    seoKeywords: ['christmas gifts 2025', 'holiday gift guide', 'christmas shopping', 'best christmas gifts']
  }
];

export const BlogPostPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const post = blogPosts.find(p => p.slug === slug);

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="text-center py-12">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Blog post not found
          </h2>
          <p className="text-gray-600 mb-4">
            The blog post you're looking for doesn't exist.
          </p>
          <Link to="/blog">
            <Button>Back to Blog</Button>
          </Link>
        </Card>
      </div>
    );
  }

  return (
    <>
      <SEO
        title={post.title}
        description={post.description}
        keywords={post.seoKeywords}
        type="article"
        publishedTime={post.publishedAt}
        image={post.image}
        url={`https://getwishful.com/blog/${post.slug}`}
      />

      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Button */}
          <div className="mb-8">
            <Link to="/blog">
              <Button variant="outline" size="sm">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Blog
              </Button>
            </Link>
          </div>

          {/* Article Header */}
          <article className="mb-12">
            <div className="mb-8">
              <div className="flex items-center space-x-4 text-sm text-gray-500 mb-4">
                <span className="bg-brown-100 text-brown-800 px-2 py-1 rounded">
                  {post.category}
                </span>
                <div className="flex items-center">
                  <Calendar className="h-4 w-4 mr-1" />
                  {new Date(post.publishedAt).toLocaleDateString()}
                </div>
                <div className="flex items-center">
                  <Clock className="h-4 w-4 mr-1" />
                  {post.readingTime}
                </div>
                <span>By {post.author}</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                {post.title}
              </h1>
              
              <p className="text-xl text-gray-600 mb-6">
                {post.description}
              </p>
            </div>

            {/* Featured Image */}
            <div className="mb-8">
              <img
                src={post.image}
                alt={post.imageAlt}
                className="w-full h-64 md:h-96 object-cover rounded-lg"
              />
            </div>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <div dangerouslySetInnerHTML={{ __html: post.content.replace(/\n/g, '<br>') }} />
            </div>

            {/* Tags */}
            <div className="mt-8 pt-8 border-t border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Tags</h3>
              <div className="flex flex-wrap gap-2">
                {post.tags.map((tag) => (
                  <span
                    key={tag}
                    className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </article>

          {/* CTA */}
          <Card className="p-8 bg-white text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Ready to Start Planning?
            </h2>
            <p className="text-gray-600 mb-6">
              Put these gift ideas into practice with your own wishlist.
            </p>
            <Link to="/dashboard">
              <Button size="lg">
                Create Your Wishlist
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </Card>
        </div>
      </div>
    </>
  );
};
